/**
 * Hosts `useAssistant` for the drawer, outside the sheet's content (#765,
 * spec §8.0). Radix unmounts that content on close, and a question in flight
 * — its promise, its idempotency key, the turn being polled — must not go
 * with it: a reopen before history lists the turn would otherwise take the
 * same question again under a new key, a second LLM call and a second charge
 * (Codex P2, round 23). The panel reads the result through context. Not
 * remounted per subject: the hook resets itself on a subject or organization
 * change, and the sheet around it stays put.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { buildSubjectKey, type AssistantSubject } from '@aica/api-types/schemas';
import { assistantErrorKeys } from '@/lib/assistant/error-keys';
import { useToast } from '@/contexts/ToastContext';
import { useAssistantDrawerSafe } from '@/contexts/AssistantDrawerContext';
import { formatResetTime } from '@/lib/assistant/reset-time';
import {
  useAssistant,
  type UseAssistantParams,
  type UseAssistantResult,
} from '@/hooks/assistant/useAssistant';

export interface AssistantSessionValue extends UseAssistantResult {
  /**
   * What this session is about. The panel reads its contour from here, not
   * from the drawer: on the `/assistant` page the drawer's topic may be a
   * record while the page asks about the whole base.
   */
  subject: AssistantSubject;
  /** What the user has typed for the CURRENT topic and not yet sent. */
  draft: string;
  setDraft: (next: string) => void;
  /**
   * Moves the unsent question to `subject` — for a deliberate change of LEVEL
   * in the scope switcher, where the question stays the same and only what it
   * is asked about changes (spec §10.16). Browsing to another record does not
   * call this: record A's text must not appear in record B's panel (Codex P2,
   * round 2). A draft already waiting under `subject` is not overwritten.
   */
  carryDraftTo: (subject: AssistantSubject) => void;
}

const AssistantSessionContext = createContext<AssistantSessionValue | null>(null);

export function AssistantSession({
  children,
  reportsBusy = true,
  ...params
}: UseAssistantParams & {
  children: ReactNode;
  /**
   * Whether this session holds the DRAWER's topic still while a turn is in
   * flight. The drawer's own session does; the `/assistant` page's does not:
   * the flag is one per drawer, and a page leaving would clear it in the
   * middle of the drawer's own paid turn.
   */
  reportsBusy?: boolean;
}) {
  const assistant = useAssistant(params);
  const { toast } = useToast();
  const { t } = useTranslation('assistant');
  // While a paid turn is still running, the topic must not move (#922): a move
  // bumps the generation here and abandons the ask in flight or the turn being
  // polled, along with a deadline the server may still be working past.
  // `hasUnsettledWork` names those cases; the hook owns that judgement, the
  // provider owns the topic, and this only carries the word between them.
  //
  // All of them end by themselves — an answer, a refusal, a timeout — so the
  // hold is always temporary. A lost ask is deliberately NOT among them: it
  // has no such end, and holding the topic for it froze following for the rest
  // of the session, with the only way out (the offer button) costing the very
  // key the hold protected. Nor does it need the hold: its key survives a
  // move, so a retry made back on its subject still rides it (#937).
  //
  // Safe, not strict: the session is the drawer's today, but Ф3 plans a full
  // `/assistant` page that renders the panel without one. No drawer, no topic
  // to hold still — the report is simply not made.
  const drawerReportBusy = useAssistantDrawerSafe()?.reportBusy;
  const reportBusy = reportsBusy ? drawerReportBusy : undefined;
  const busy = assistant.hasUnsettledWork;
  useEffect(() => {
    if (!reportBusy) return;
    reportBusy(busy);
    return () => reportBusy(false);
  }, [busy, reportBusy]);

  // The draft lives here, per topic, not in the panel (spec §10.16: «черновик
  // composer при смене области — сохраняется», owner-confirmed 11.09). The
  // panel is keyed by the subject so a draft typed for record A cannot appear
  // in record B's panel (Codex P2, round 2) — which also meant a remount threw
  // it away. Keeping it keyed by subject satisfies both: A's text waits for A,
  // B opens blank. It dies with the session, so an organization change takes
  // it too.
  const subjectKey = buildSubjectKey(params.subject);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const draft = drafts[subjectKey] ?? '';
  const setDraft = useCallback(
    (next: string) =>
      setDrafts((prev) => {
        // Emptied — sent, or cleared by hand. Dropping the key instead of
        // storing '' is what keeps this map the size of "records with unsent
        // text" rather than "records visited this session": nothing else ever
        // deletes from it, and the object is copied on every keystroke.
        if (next === '') {
          if (!(subjectKey in prev)) return prev;
          const { [subjectKey]: _sent, ...rest } = prev;
          return rest;
        }
        return { ...prev, [subjectKey]: next };
      }),
    [subjectKey]
  );
  const carryDraftTo = useCallback(
    (subject: AssistantSubject) =>
      setDrafts((prev) => {
        const text = prev[subjectKey];
        const target = buildSubjectKey(subject);
        if (!text || target === subjectKey || prev[target]) return prev;
        const { [subjectKey]: _moved, ...rest } = prev;
        return { ...rest, [target]: text };
      }),
    [subjectKey]
  );
  // Deliberately not memoized: `useAssistant` returns a fresh object every
  // render, so any memo here would recompute unconditionally while reading as
  // a stability guarantee it cannot give.
  const value: AssistantSessionValue = {
    ...assistant,
    subject: params.subject,
    draft,
    setDraft,
    carryDraftTo,
  };
  // Refusals reach the user as a toast keyed by the machine code (§2.2); a
  // ceiling names its window — the per-minute and hourly ones reset at a
  // time, not "tomorrow" (Codex P2, round 3). Fired from here, not the
  // panel: the panel is unmounted with the sheet and would replay the last
  // refusal on every reopen; the session outlives the sheet. `toast` and
  // `t` are read through refs — a language switch must not replay it either
  // (owner's review, 17.09).
  const toastRef = useRef(toast);
  const translateRef = useRef(t);
  const scopeRef = useRef(params.subject.scope);
  scopeRef.current = params.subject.scope;
  useEffect(() => {
    toastRef.current = toast;
    translateRef.current = t;
  });
  useEffect(() => {
    const refusal = assistant.refusal;
    // A turn picked up from history is announced by whoever asked it; here
    // its failure shows on the turn (code-review, #1312).
    if (
      assistant.state !== 'failed' ||
      !refusal ||
      refusal.resumed ||
      assistant.blockedCode
    )
      return;
    const translate = translateRef.current;
    if (refusal.code === 'quota_exceeded' && refusal.window && refusal.window !== 'day') {
      toastRef.current.error(
        translate(
          refusal.window === 'minute'
            ? 'errors.quota_exceeded_minute'
            : 'errors.quota_exceeded_hour',
          { time: formatResetTime(refusal.resetAt) }
        )
      );
      return;
    }
    toastRef.current.error(translate(assistantErrorKeys(refusal.code, scopeRef.current)));
  }, [assistant.state, assistant.refusal, assistant.blockedCode]);
  return (
    <AssistantSessionContext.Provider value={value}>
      {children}
    </AssistantSessionContext.Provider>
  );
}

/** `null` outside a session — the scope switcher in a test, or a page without one. */
export function useAssistantSessionSafe(): AssistantSessionValue | null {
  return useContext(AssistantSessionContext);
}

export function useAssistantSession(): AssistantSessionValue {
  const context = useContext(AssistantSessionContext);
  if (!context) {
    throw new Error('useAssistantSession must be used within AssistantSession');
  }
  return context;
}
